/*
 * TonkCrashLogCard — admin panel view of client-side Tonk render crashes
 * caught by TonkPageBoundary and POSTed to /api/errors/tonk-crash.
 *
 * Iter 154 (2026-07-24). Follows the "user reported X, gave us zero repro
 * steps, we hunted blind" pain we hit for the "black screen after tonk-out"
 * bug — going forward every future crash reports its own stack trace with
 * table_id, user, and viewport attached.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ChevronDown, ChevronRight, Check, CheckCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtWhen = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

const TonkCrashLogCard = () => {
    const [rows, setRows] = useState([]);
    const [weekTotal, setWeekTotal] = useState(0);
    const [weekUnack, setWeekUnack] = useState(0);
    const [includeAck, setIncludeAck] = useState(false);
    const [openIds, setOpenIds] = useState({});
    const [busy, setBusy] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/api/admin/tonk-crash-log?include_ack=${includeAck ? 'true' : 'false'}`,
                { withCredentials: true },
            );
            setRows(data.rows || []);
            setWeekTotal(data.week_total || 0);
            setWeekUnack(data.week_unack || 0);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load crash log');
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [includeAck]);

    const acknowledge = async (id) => {
        setBusy(id);
        try {
            await axios.post(`${API}/api/admin/tonk-crash-log/${id}/acknowledge`, {}, { withCredentials: true });
            toast.success('Marked as reviewed');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally { setBusy(null); }
    };

    const acknowledgeAll = async () => {
        if (!window.confirm(`Mark all ${weekUnack} unreviewed crash reports as reviewed?`)) return;
        setBusy('all');
        try {
            const { data } = await axios.post(
                `${API}/api/admin/tonk-crash-log/acknowledge-all`,
                {}, { withCredentials: true },
            );
            toast.success(`Cleared ${data.count} crash reports`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally { setBusy(null); }
    };

    // Bucket by message signature so the admin can see repeated patterns.
    const patterns = useMemo(() => {
        const groups = new Map();
        for (const r of rows) {
            const sig = (r.message || 'unknown').slice(0, 80);
            if (!groups.has(sig)) groups.set(sig, []);
            groups.get(sig).push(r);
        }
        return [...groups.entries()]
            .map(([sig, list]) => ({ sig, list, count: list.length }))
            .sort((a, b) => b.count - a.count);
    }, [rows]);

    // Nothing to show and no one has crashed in the last week → hide the card
    // entirely so the admin dashboard doesn't get cluttered.
    if (!loading && rows.length === 0 && weekTotal === 0) return null;

    return (
        <Card className="bg-zinc-900/60 border-rose-500/30" data-testid="tonk-crash-log-card">
            <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-rose-400" />
                        <span className="text-sm font-black tracking-wider uppercase">Tonk Client Crashes</span>
                        {weekUnack > 0 && (
                            <Badge className="bg-rose-500 text-white font-black text-[10px] px-1.5 py-0"
                                data-testid="tonk-crash-log-badge">
                                {weekUnack} new
                            </Badge>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px]">
                        <button
                            type="button"
                            className="px-2 py-1 rounded bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"
                            onClick={() => setIncludeAck((v) => !v)}
                            data-testid="tonk-crash-log-toggle-ack"
                        >
                            {includeAck ? 'Hide reviewed' : 'Show reviewed'}
                        </button>
                        {weekUnack > 0 && (
                            <button
                                type="button"
                                className="px-2 py-1 rounded bg-emerald-500 text-zinc-900 font-black hover:brightness-110 disabled:opacity-50"
                                onClick={acknowledgeAll}
                                disabled={busy === 'all'}
                                data-testid="tonk-crash-log-ack-all"
                            >
                                <CheckCheck className="w-3 h-3 inline mr-1" />
                                Ack all
                            </button>
                        )}
                    </div>
                </CardTitle>
                <p className="text-[10px] text-zinc-500 mt-1">
                    Last 7 days: {weekTotal} crashes ({weekUnack} unreviewed). Each row
                    is a React render crash caught by TonkPageBoundary — the player
                    saw a &quot;Table hiccup&quot; fallback screen, not a black void.
                </p>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <p className="text-xs text-zinc-500">Loading…</p>
                ) : rows.length === 0 ? (
                    <p className="text-xs text-emerald-400/80">✓ No crashes to review — Tonk has been humming along.</p>
                ) : (
                    <div className="space-y-2">
                        {patterns.length > 1 && (
                            <div className="rounded-lg bg-black/40 border border-white/5 p-2">
                                <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1 font-bold">
                                    Repeat patterns
                                </div>
                                <div className="space-y-0.5">
                                    {patterns.filter((p) => p.count > 1).map((p) => (
                                        <div key={p.sig} className="text-[10px] font-mono text-rose-300 flex gap-2">
                                            <span className="font-black text-rose-400 w-6 shrink-0">×{p.count}</span>
                                            <span className="truncate">{p.sig}</span>
                                        </div>
                                    ))}
                                    {patterns.every((p) => p.count === 1) && (
                                        <div className="text-[10px] text-zinc-500">All crashes are unique.</div>
                                    )}
                                </div>
                            </div>
                        )}
                        {rows.map((r) => {
                            const open = !!openIds[r.id];
                            return (
                                <div
                                    key={r.id}
                                    className={`rounded-lg border p-2 text-xs ${
                                        r.acknowledged
                                            ? 'bg-zinc-950 border-zinc-800 opacity-60'
                                            : 'bg-rose-500/5 border-rose-500/30'
                                    }`}
                                    data-testid={`tonk-crash-row-${r.id}`}
                                >
                                    <div className="flex items-start gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setOpenIds((s) => ({ ...s, [r.id]: !open }))}
                                            className="mt-0.5 text-zinc-400 hover:text-white"
                                            aria-label="Toggle details"
                                        >
                                            {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-rose-300 font-mono font-black text-[11px] truncate max-w-[420px]">
                                                    {r.message || 'Unknown error'}
                                                </span>
                                                {r.acknowledged && (
                                                    <Badge className="bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[9px] px-1 py-0">
                                                        reviewed
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 text-[10px] text-zinc-500 mt-0.5">
                                                <span>{fmtWhen(r.created_at)}</span>
                                                {r.user_name && <span>· {r.user_name}</span>}
                                                {r.table_id && <span>· table {r.table_id.slice(-8)}</span>}
                                                {r.sw_cache && <span>· sw {r.sw_cache}</span>}
                                            </div>
                                        </div>
                                        {!r.acknowledged && (
                                            <Button
                                                size="sm"
                                                onClick={() => acknowledge(r.id)}
                                                disabled={busy === r.id}
                                                className="bg-emerald-500 text-zinc-900 hover:bg-emerald-400 font-black text-[10px] px-2 h-7"
                                                data-testid={`tonk-crash-ack-${r.id}`}
                                            >
                                                <Check className="w-3 h-3 mr-1" /> Reviewed
                                            </Button>
                                        )}
                                    </div>
                                    {open && (
                                        <div className="mt-2 space-y-1.5 pl-5">
                                            {r.stack && (
                                                <details open>
                                                    <summary className="text-[10px] text-zinc-400 font-bold cursor-pointer">Stack trace</summary>
                                                    <pre className="text-[10px] font-mono text-zinc-300 whitespace-pre-wrap break-all bg-black/50 p-2 rounded mt-1 max-h-64 overflow-y-auto">{r.stack}</pre>
                                                </details>
                                            )}
                                            {r.component_stack && (
                                                <details>
                                                    <summary className="text-[10px] text-zinc-400 font-bold cursor-pointer">Component stack</summary>
                                                    <pre className="text-[10px] font-mono text-zinc-300 whitespace-pre-wrap break-all bg-black/50 p-2 rounded mt-1 max-h-64 overflow-y-auto">{r.component_stack}</pre>
                                                </details>
                                            )}
                                            {(r.user_agent || r.url) && (
                                                <div className="text-[10px] text-zinc-500 font-mono break-all">
                                                    {r.url && <div>{r.url}</div>}
                                                    {r.user_agent && <div className="opacity-70">{r.user_agent}</div>}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default TonkCrashLogCard;
